import {
    IAuditInstanceAdditionalData,
} from '@/audit/interfaces/AuditInterfaces';
import {
    EntityEnum,
    IntegrationSourceEnum,
    getEntityEnumDescription,
    getIntegrationSourceEnumDescription,
} from '@c2b/muven-commons';

import { 
    AuditStatusEnum, 
    AuditSeverityEnum, 
    getAuditSeverityEnumDescription, 
    getAuditStatusEnumDescription 
} from '@c2b/commons'

import { SubscriberModel } from '../../subscriber/models/SubscriberModel';
import { ChannelModel } from '../../channel/models/ChannelModel';

/**
 * Quem gerou o registro, para a identidade visual da coluna "Interação".
 * 'generic-channel' é o canal sem idChannel: sabemos que veio de um canal, não de qual.
 */
export type AuditActorKind = 'channel' | 'backend' | 'muven' | 'generic-channel'

export class AuditInstanceModel {
    _id?: string = undefined;
    entity: EntityEnum;
    modificationSource: IntegrationSourceEnum;
    idChannel?: string | number = undefined;
    channelName: string = '';
    channel?: ChannelModel = undefined;
    idEntity?: string | number | object = undefined;
    name: string = '';
    description: string = '';
    status: AuditStatusEnum = AuditStatusEnum.RUNNING;
    date: Date | string | undefined = undefined;
    additionalData?: IAuditInstanceAdditionalData = undefined;
    severity: AuditSeverityEnum;
    clientId: string = '';
    userId: string = '';
    userEmail: string = '';
    loadingListAction: boolean = false;
    collapsed: boolean = true;

    constructor(a?: Partial<AuditInstanceModel>) {
        Object.assign(this, a);

        this._parseDate();
    }

    getInteractionDescription(subscriber: SubscriberModel):string {
        if (this.isFromAgent()) {
            return this.channelName
        }

        if (this.modificationSource === IntegrationSourceEnum.Backend) {
            return "Retaguarda"
        }

        return this.getModificationSourceDescription()
    }

    setChannelName(n:string):void {
        this.channelName = n
    }

    setChannel(channel:ChannelModel):void {
        this.channel = channel
        this.setChannelName(channel.name)
    }

    // Mesma ordem de decisão de getInteractionDescription.
    getActorKind():AuditActorKind {
        if (this.isFromAgent()) {
            return 'channel'
        }

        if (this.modificationSource === IntegrationSourceEnum.Backend) {
            return 'backend'
        }

        if (this.modificationSource === IntegrationSourceEnum.Channel) {
            return 'generic-channel'
        }

        return 'muven'
    }

    isStatusRunning(): boolean {
        return this.status === AuditStatusEnum.RUNNING;
    }

    isStatusError(): boolean {
        return this.status === AuditStatusEnum.ERROR;
    }

    isStatusCompleted(): boolean {
        return this.status === AuditStatusEnum.COMPLETED;
    }

    isStatusWarning(): boolean {
        return this.status === AuditStatusEnum.WARNING;
    }

    isSeverityLow(): boolean {
        return this.severity === AuditSeverityEnum.LOW
    }

    isSeverityMedium(): boolean {
        return this.severity === AuditSeverityEnum.MEDIUM
    }

    isSeverityHigh(): boolean {
        return this.severity === AuditSeverityEnum.HIGH
    }

    isEntityOrder(): boolean {
        return this.entity === EntityEnum.Order
    }

    isEntityProduct(): boolean {
        return this.entity === EntityEnum.Product
    }

    isEntityCategory(): boolean {
        return this.entity === EntityEnum.Category
    }

    isEntityBrand(): boolean {
        return this.entity === EntityEnum.Brand
    }

    isEntityDeliveryCompany(): boolean {
        return this.entity === EntityEnum.DeliveryCompany
    }

    isEntityPayment(): boolean {
        return this.entity === EntityEnum.Payment
    }

    isEntitySystem(): boolean {
        return this.entity === EntityEnum.System
    }

    hasIdEntity(): boolean {
        return !!this.idEntity && (typeof this.idEntity !== "object");
    }

    hasAdditionalData(): boolean {
        return !!this.additionalData;
    }

    getInteraction():string {
        if (this.isFromAgent()) {
            return this.getModificationSourceDescription()
        }

        return this.channelName
    }

    getSeverityDescription(): string {
        return getAuditSeverityEnumDescription(this.severity)
    }

    getModificationSourceDescription(): string {
        return getIntegrationSourceEnumDescription(this.modificationSource)
    }

    getEntityEnumDescription(): string {
        return getEntityEnumDescription(this.entity);
    }

    getStatusEnumDescription(): string {
        return getAuditStatusEnumDescription(this.status)
    }

    isFromAgent():boolean {
        return !!this.idChannel
    }

    private _parseDate(): void {
        if (typeof this.date !== "string") {
            return;
        }

        this.date = new Date(this.date);
    }
}
